import { useMemo } from 'react';
import { MagnifyingGlassIcon, TableCellsIcon, GlobeAltIcon, XMarkIcon } from '@heroicons/react/24/outline';
import useDataviewerStore, { dataviewerActions } from '@/stores/dataviewerStore';
import DataviewerFilters from './DataviewerFilters';
import DataviewerTable from './DataviewerTable';
import DataviewerMap from './DataviewerMap';

const DataviewerRoute = () => {
  const datasets = useDataviewerStore(s => s.datasets);
  const isLoadingDatasets = useDataviewerStore(s => s.isLoadingDatasets);
  const selectedDataset = useDataviewerStore(s => s.selectedDataset);
  const isLoadingDataset = useDataviewerStore(s => s.isLoadingDataset);
  const searchQuery = useDataviewerStore(s => s.searchQuery);
  const filters = useDataviewerStore(s => s.filters);
  const viewMode = useDataviewerStore(s => s.viewMode);

  const filteredDatasets = useMemo(() => {
    if (!datasets) return [];
    const q = searchQuery.toLowerCase();
    return datasets.filter(d => {
      if (!d.has_cost_data) return false;
      if (!q) return true;
      return (
        d.name.toLowerCase().includes(q) ||
        d.slug.toLowerCase().includes(q) ||
        d.category.toLowerCase().includes(q) ||
        d.sources.some(s => s.toLowerCase().includes(q))
      );
    });
  }, [datasets, searchQuery]);

  const filteredRows = useMemo(() => {
    if (!selectedDataset) return [];
    return selectedDataset.rows.filter(row =>
      Object.entries(filters).every(([k, v]) => !v?.length || v.includes(row[k])),
    );
  }, [selectedDataset, filters]);

  return (
    <div className="flex flex-1 min-w-0 h-full bg-gray-50">
      {/* Left panel — dataset list */}
      <div className="w-72 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-100">
          <h1 className="text-base font-semibold text-gray-800 mb-3">Data Viewer</h1>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search modules..."
              className="input input-sm input-bordered w-full !pl-10"
              value={searchQuery}
              onChange={e => dataviewerActions.setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoadingDatasets && <div className="p-4 text-center text-sm text-gray-400">Loading...</div>}
          {!isLoadingDatasets && filteredDatasets.length === 0 && (
            <div className="p-4 text-center text-sm text-gray-400">
              {searchQuery ? 'No datasets match your search.' : 'No datasets available.'}
            </div>
          )}
          {filteredDatasets.map(dataset => {
            const isSelected = selectedDataset?.slug === dataset.slug && selectedDataset?.category === dataset.category;
            return (
              <button
                type="button"
                key={`${dataset.category}/${dataset.slug}`}
                className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                  isSelected ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
                }`}
                onClick={() => dataviewerActions.selectDataset(dataset.category, dataset.slug)}
              >
                <p className={`text-sm font-medium ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>
                  {dataset.name}
                </p>
                <p className="text-xs text-gray-400 mt-0.5 capitalize">
                  {dataset.category.replace(/-/g, ' ')} · {dataset.sources.length}{' '}
                  {dataset.sources.length === 1 ? 'source' : 'sources'}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right panel — detail */}
      <div className="flex-1 overflow-y-auto p-6">
        {!selectedDataset && !isLoadingDataset && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <GlobeAltIcon className="h-12 w-12 mb-3" />
            <p className="text-sm">Select a dataset to explore its coverage and cost data.</p>
          </div>
        )}

        {isLoadingDataset && (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading dataset...</div>
        )}

        {selectedDataset && !isLoadingDataset && (
          <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">{selectedDataset.name}</h2>
                <p className="text-xs text-gray-400 capitalize mt-0.5">
                  {selectedDataset.category.replace(/-/g, ' ')} · {selectedDataset.rows.length}{' '}
                  {selectedDataset.rows.length === 1 ? 'source' : 'sources'}
                </p>
              </div>
            </div>

            {/* View toggle */}
            <div className="flex items-center gap-2">
              <div className="join">
                <button
                  type="button"
                  className={`join-item btn btn-sm ${viewMode === 'table' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => dataviewerActions.setViewMode('table')}
                >
                  <TableCellsIcon className="h-4 w-4" />
                  Table
                </button>
                <button
                  type="button"
                  className={`join-item btn btn-sm ${viewMode === 'map' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => dataviewerActions.setViewMode('map')}
                >
                  <GlobeAltIcon className="h-4 w-4" />
                  Map
                </button>
              </div>

              {Object.keys(filters).length > 0 && (
                <button
                  type="button"
                  className="btn btn-xs btn-ghost text-gray-400 gap-1"
                  onClick={dataviewerActions.clearFilters}
                >
                  <XMarkIcon className="h-3 w-3" />
                  Clear filters
                </button>
              )}
            </div>

            {/* Filters */}
            <DataviewerFilters filterOptions={selectedDataset.filter_options} />

            {/* Content */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              {viewMode === 'table' ? (
                <DataviewerTable
                  rows={filteredRows}
                  metadataKeys={selectedDataset.metadata_keys}
                  costUnits={selectedDataset.cost_units}
                />
              ) : (
                <DataviewerMap rows={filteredRows} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataviewerRoute;
